import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button, CircularProgress, Snackbar, Card, CardContent,
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';

const UpdateFeeBalance = () => {
  const { state } = useLocation();
  const { learner } = state || {};
  const navigate = useNavigate();

  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [term, setTerm] = useState('Term 1');
  const [amountPaid, setAmountPaid] = useState('');
  const [studentFees, setStudentFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (learner?.parentId && learner?.schoolId) fetchStudentFees();
  }, [learner]);

  const fetchStudentFees = async () => {
    try {
      setLoading(true);

      if (!learner?.parentId || !learner?.schoolId) {
        throw new Error(`Missing IDs - parentId: ${learner?.parentId}, schoolId: ${learner?.schoolId}`);
      }

      const response = await axios.get(
        `https://kidsm.vercel.app/api/schools/parent/${learner.parentId}/fees/${learner.schoolId}`
      );

      if (response.data.success) {
        setStudentFees(response.data.fees);
      } else {
        throw new Error(response.data.message || 'Failed to fetch fees');
      }
    } catch (error) {
      console.error('Detailed fee fetch error:', {
        error: error.message,
        learnerData: learner,
        endpoint: `parent/${learner?.parentId}/fees/${learner?.schoolId}`,
      });
      setSnackbar({ open: true, message: `Failed to load fees: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!year || !term || !amountPaid) {
      setSnackbar({ open: true, message: 'Please fill all fields', severity: 'error' });
      return;
    }

    if (isNaN(amountPaid)) {
      setSnackbar({ open: true, message: 'Amount must be a number', severity: 'error' });
      return;
    }

    try {
      const response = await axios.post(
        'https://kidsm.vercel.app/api/schools/update-student-fee',
        {
          learnerId: learner._id,
          year,
          term,
          amountPaid: parseFloat(amountPaid),
          schoolId: learner.schoolId,
          className: learner.class,
        }
      );

      setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      fetchStudentFees();
      setAmountPaid('');
    } catch (error) {
      console.error('Error updating student fee:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update student fee',
        severity: 'error',
      });
    }
  };

  if (!learner) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#e74c3c' }}>
          Error: No learner data provided
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 2, textAlign: 'center' }}>
          Update Fee for {learner.fullName}
        </Typography>
        <Typography sx={{ fontSize: '1.125rem', color: '#555', mb: 4, textAlign: 'center' }}>
          Class: {learner.class}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="e.g. 2023"
            type="number"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Term"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Term 1, Term 2, or Term 3"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Amount Paid (KES)"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            placeholder="Enter amount paid"
            type="number"
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          sx={{
            bgcolor: '#6200ee',
            borderRadius: 2,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold',
            mb: 4,
            '&:hover': { bgcolor: '#3700b3' },
          }}
        >
          Update Fee
        </Button>

        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
          Payment History
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : studentFees.length === 0 ? (
          <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
            No payment history found
          </Typography>
        ) : (
          studentFees.map((fee) => (
            <Card key={fee._id} sx={{ bgcolor: '#fff', borderRadius: 2, mb: 2, borderLeft: '4px solid #6200ee' }}>
              <CardContent>
                <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>Year: {fee.year}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>Term: {fee.term}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                  Total Amount: KES {fee.totalAmount.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                  Amount Paid: KES {fee.amountPaid.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: fee.balance >= 0 ? 'red' : 'green',
                  }}
                >
                  Balance: KES {Math.abs(fee.balance).toLocaleString()} {fee.balance >= 0 ? '(Due)' : '(Credit)'}
                </Typography>
              </CardContent>
            </Card>
          ))
        )}
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        sx={{ bgcolor: snackbar.severity === 'success' ? '#4CAF50' : '#e74c3c' }}
      />
    </Container>
  );
};

export default UpdateFeeBalance;